import Skeleton from '@/components/UI/Skeleton';
import { usePageContext } from '@/renderer/utils/usePageContext';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AllSportsIcon,
  CBBIcon,
  CFBIcon,
  NFLIcon,
  // RASPlusIcon,
} from './ProductIcons';
import { sportIdFromName, sportNameFromId } from './productHelpers';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Product {
  name: string;
  href: string;
  startsWithHref?: string;
  icon?: (props: React.ComponentPropsWithoutRef<'svg'>) => JSX.Element;
  sub?: string;
  sportId: API.Id;
  tag?: string;
}

const PRODUCTS: Product[] = [
  {
    name: 'All Services',
    href: '/order/',
    icon: AllSportsIcon,
    sportId: 8,
  },
  {
    name: 'College Basketball',
    href: '/order/cbb',
    icon: CBBIcon,
    sportId: 2,
  },
  {
    name: 'NFL',
    href: '/order/nfl',
    icon: NFLIcon,
    sportId: 3,
  },
  {
    name: 'College Football',
    href: '/order/cfb',
    icon: CFBIcon,
    sportId: 1,
  },
  //   {
  //   name: 'RAS+',
  //   href: '/order/plus',
  //   icon: RASPlusIcon,
  //   sportId: 8,
  //   tag: 'ras-plus',
  // },
  // {
  //   name: 'Betting Talk',
  //   href: '/order/betting-talk',
  //   icon: BettingTalkIcon,
  //   sportId: 8,
  //   tag: 'betting-talk',
  // },
];

const ProductMenu = ({
  products,
  allProducts,
  product,
  isLoading,
  routeParams,
}: {
  products?: API.MembershipProduct<true>[];
  allProducts?: API.MembershipProduct<true>[];
  product?: API.MembershipProduct<true>;
  isLoading: boolean;
  routeParams: Record<string, string>;
}) => {
  const { urlParsed } = usePageContext();
  const ref = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = () => {
    if (!ref.current) {
      return;
    }

    setShowLeftArrow(ref.current.scrollLeft > 0);
    setShowRightArrow(ref.current.scrollLeft < ref.current.scrollWidth - ref.current.clientWidth);
  };

  useEffect(() => {
    const handleResize = () => {
      updateArrows();
    };

    const observer = new ResizeObserver(handleResize);
    if (ref.current) {
      observer.observe(ref.current);
    }

    const handleScroll = () => {
      updateArrows();
    };

    if (ref.current) {
      ref.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
        ref.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [ref]);

  useEffect(() => {
    updateArrows();
  }, [ref.current?.clientWidth, ref.current?.scrollLeft]);

  // Derive which sportIds currently have at least one product with a non-hidden offer.
  // Uses allProducts (full unfiltered list) so the sport tabs remain visible even when
  // the page is filtered to a single sport. Falls back to products if allProducts not yet set.
  // null means still loading.
  const activeSportIds = useMemo<Set<number> | null>(() => {
    const raw = allProducts ?? products;
    // During SSR, useRequest may return the raw paginated response object; extract .data if so
    const source: API.MembershipProduct<true>[] | null = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as any)?.data)
        ? (raw as any).data
        : null;
    if (!source) {
      return null;
    }
    const ids = new Set<number>();
    source.forEach((prod) => {
      if (prod.offers?.some((o) => !o.hidden)) {
        prod.sportIds?.forEach((id) => ids.add(id));
      }
    });
    return ids;
  }, [allProducts, products]);

  // Only render menu items for sports that currently have available products.
  // "All Services" is only shown when 2+ distinct sports are active.
  const visibleMenuItems = useMemo(() => {
    if (activeSportIds === null) {
      return PRODUCTS; // still loading — show all
    }
    return PRODUCTS.filter(({ sportId, name }) => {
      if (name === 'All Services') {
        // Count sports excluding the catch-all (sportId 8)
        const realSports = [...activeSportIds].filter((id) => id !== 8);
        return realSports.length > 1;
      }
      return activeSportIds.has(sportId);
    });
  }, [activeSportIds]);

  const filteredProducts =
    !!products &&
    products.length > 1 &&
    products.filter((prod, idx) => {
      if (routeParams.sportId) {
        const sportId = sportIdFromName(routeParams.sportId);

        if (typeof sportId === 'number') {
          return prod.sportIds?.includes(sportId);
        }
      }

      if (routeParams.tag) {
        return prod.tags?.includes(routeParams.tag);
      }

      if (product?.sportIds?.length && prod.sportIds?.length) {
        return prod.sportIds?.some((id) => product.sportIds?.includes(id));
      }

      if (product?.id) {
        return prod.id === product.id;
      }

      return idx === 0;
    });

  const showSportNav = visibleMenuItems.length > 1;
  const showSubPicker = !!filteredProducts && filteredProducts.length > 1;

  if (!showSportNav && !showSubPicker) {
    return null;
  }

  return (
    <nav className="group/product-menu sticky">
      {showLeftArrow && (
        <div className="absolute top-12 left-2 z-10 hidden items-center justify-center rounded-full bg-zinc-300 p-1.5 opacity-95 shadow-sm group-hover/product-menu:flex dark:bg-zinc-900 sm:p-2">
          <ChevronLeftIcon className="size-6 shrink-0" />
        </div>
      )}
      {showRightArrow && (
        <div className="absolute top-12 right-2 z-10 hidden items-center justify-center rounded-full bg-zinc-300 p-1.5 opacity-95 shadow-sm group-hover/product-menu:flex dark:bg-zinc-900 sm:p-2">
          <ChevronRightIcon className="size-6 shrink-0" />
        </div>
      )}
      {showSportNav && (
        <section
          className="scrollbar-hidden relative flex justify-between overflow-x-auto border-zinc-900/5 border-b bg-zinc-100 py-2 dark:border-zinc-50/5 dark:bg-zinc-950 sm:py-4"
          ref={ref}
        >
          <div className="flex flex-1 items-stretch justify-center">
            <div className="mx-6 flex items-start space-x-6 sm:space-x-8">
              {visibleMenuItems.map(({ name, href, sub, icon, sportId, tag }) => {
                const IconComponent = icon;

                return (
                  <a
                    key={href}
                    href={href}
                    data-active={
                      urlParsed.pathnameOriginal === href ||
                      product?.sportIds?.includes(sportId) ||
                      (product?.tags?.includes(tag) && product.tags.length === 1)
                    }
                    className="group inline-flex items-center border-b-2 border-transparent p-1 text-xs font-normal text-zinc-600 transition hover:text-zinc-700 data-[active=true]:border-zinc-500 data-[active=true]:font-semibold data-[active=true]:text-zinc-900 dark:text-zinc-200 dark:hover:text-zinc-300 dark:data-[active=true]:text-white"
                  >
                    <div className="flex flex-col items-center space-y-1">
                      {!!IconComponent && (
                        <IconComponent
                          height={40}
                          className="size-10 fill-zinc-700 transition sm:size-16 hover:scale-105 dark:fill-zinc-200 dark:group-data-[active=true]:fill-white dark:group-hover:fill-zinc-100 group-data-[active=true]:fill-zinc-950 group-hover:fill-zinc-800"
                          aria-hidden="true"
                        />
                      )}
                      <div className="flex flex-col items-center">
                        <span className="text-center opacity-80">{name}</span>
                        <span className="h-4 text-emerald-500 dark:text-emerald-400">{sub}</span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}
      {showSubPicker && (
        <section className="border-b border-zinc-900/5 bg-zinc-50 py-1.5 shadow-xs sm:py-3 dark:border-zinc-50/5 sm:dark:bg-zinc-900">
          <div className="mx-auto flex w-fit flex-row items-center justify-center space-x-4 rounded-sm bg-zinc-50 p-1 shadow-sm ring-1 ring-zinc-900/10 dark:bg-zinc-700">
            {!products?.length && (
              <a
                href="/order"
                className="rounded-sm px-4 py-2.5 text-xs font-medium text-white data-[active=true]:bg-emerald-500"
                data-active={true}
              >
                <Skeleton loading={isLoading}>Service Not Found</Skeleton>
              </a>
            )}
            {filteredProducts.map((prod, idx) => (
              <a
                key={prod.id}
                href={`/order/${sportNameFromId(prod.sportIds?.[0])}/${prod.id}`.replace('//', '/')}
                className="rounded-sm px-4 py-2.5 text-xs font-medium text-zinc-500 hover:text-zinc-800 data-[active=true]:bg-zinc-700 data-[active=true]:text-white dark:text-zinc-300 dark:hover:text-zinc-100 dark:data-[active=true]:bg-zinc-300 dark:data-[active=true]:text-zinc-950"
                data-active={product?.id === prod.id}
              >
                {prod.title || `Service ${idx}`}
              </a>
            ))}
          </div>
        </section>
      )}
    </nav>
  );
};

export default ProductMenu;
